import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid,
  Skeleton,
  TextField,
} from "@mui/material";
import {
  CheckCircle,
  LocalShipping,
  Storefront,
  Person,
  Payment,
  ArrowBack,
  ArrowForward,
  Timer,
  MonetizationOn,
  RadioButtonUnchecked,
  AccessTime,
  Warning,
} from "@mui/icons-material";
import { GoogleMap, useJsApiLoader, Marker, Polyline } from "@react-google-maps/api";
import api from "../../api/axios";
import socket from "../../socket";
import { useSnackbar } from "../../utils/SnackbarProvider";
import LoadingScreen from "../../components/LoadingScreen";
import { fetchRoadRoutePath } from "../../utils/routePath";
import { getLatLng, getAddressText } from "../../utils/geo";
import { TRACKING_VENDOR_PREPARING } from "../../utils/orderTrackingStages";

function formatCurrency(value) {
  if (typeof value === "number") return `₹${value.toLocaleString("en-IN")}`;
  if (value === 0) return "₹0";
  return value || "—";
}

const MAP_LIBRARIES = ["places", "marker", "routes"];

function RouteMap({ pickupAddress, dropoffAddress, pickupCoords, dropoffCoords }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const [roadPath, setRoadPath] = useState(null);
  const mapRef = useRef(null);
  const [pickup, setPickup] = useState(pickupCoords || null);
  const [dropoff, setDropoff] = useState(dropoffCoords || null);
  const [pendingGeocodes, setPendingGeocodes] = useState(0);
  const geocodeAttemptedRef = useRef({ pickup: false, dropoff: false });

  // Update from direct coords — mark geocode as attempted so we don't double-fetch
  useEffect(() => {
    if (pickupCoords) {
      setPickup(pickupCoords);
      geocodeAttemptedRef.current.pickup = true;
    }
  }, [pickupCoords]);

  useEffect(() => {
    if (dropoffCoords) {
      setDropoff(dropoffCoords);
      geocodeAttemptedRef.current.dropoff = true;
    }
  }, [dropoffCoords]);

  // Geocode address fallback (only if no direct coords provided)
  useEffect(() => {
    if (!isLoaded || !window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();

    if (!pickup && pickupAddress && !geocodeAttemptedRef.current.pickup) {
      geocodeAttemptedRef.current.pickup = true;
      setPendingGeocodes(prev => prev + 1);
      geocoder.geocode({ address: pickupAddress }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          const loc = results[0].geometry.location;
          setPickup({ lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat, lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng });
        }
        setPendingGeocodes(prev => Math.max(0, prev - 1));
      });
    }

    if (!dropoff && dropoffAddress && !geocodeAttemptedRef.current.dropoff) {
      geocodeAttemptedRef.current.dropoff = true;
      setPendingGeocodes(prev => prev + 1);
      geocoder.geocode({ address: dropoffAddress }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          const loc = results[0].geometry.location;
          setDropoff({ lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat, lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng });
        }
        setPendingGeocodes(prev => Math.max(0, prev - 1));
      });
    }
  }, [isLoaded, pickupAddress, dropoffAddress, pickup, dropoff]);

  // Fetch route via OSRM
  useEffect(() => {
    let cancelled = false;

    const loadRoute = async () => {
      if (!isLoaded || !window.google?.maps || !pickup || !dropoff || pendingGeocodes > 0) {
        setRoadPath(null);
        return;
      }

      try {
        const path = await fetchRoadRoutePath(pickup, dropoff);
        if (!cancelled) {
          setRoadPath(path);
        }
      } catch (error) {
        if (!cancelled) {
          setRoadPath(null);
        }
      }
    };

    loadRoute();
    return () => { cancelled = true; };
  }, [isLoaded, pickup, dropoff, pendingGeocodes]);

  const fitBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    if (pickup) bounds.extend(pickup);
    if (dropoff) bounds.extend(dropoff);
    if (!bounds.isEmpty()) map.fitBounds(bounds);
  }, [pickup, dropoff]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    requestAnimationFrame(fitBounds);
  }, [fitBounds]);

  // Re-fit when coordinates change after map is already loaded
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    fitBounds();
  }, [isLoaded, fitBounds]);

  if (loadError) return <RouteMapFallback pickupAddress={pickupAddress} dropoffAddress={dropoffAddress} />;
  if (!isLoaded || pendingGeocodes > 0) return <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} />;
  if (!pickup && !dropoff && !pickupAddress && !dropoffAddress) {
    return (
      <Paper sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, textAlign: "center" }}>
        <LocalShipping sx={{ fontSize: 28, color: "#94a3b8", mb: 0.5 }} />
        <Typography variant="caption" sx={{ color: "#94a3b8" }}>Route unavailable — no location data</Typography>
      </Paper>
    );
  }

  const hasRoute = pickup && dropoff;
  const center = pickup || dropoff || { lat: 20.5937, lng: 78.9629 };

  return (
    <Box sx={{ borderRadius: 2, overflow: "hidden", height: 350, width: "100%" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={hasRoute ? 13 : 12}
        onLoad={onMapLoad}
        options={{ disableDefaultUI: true, zoomControl: true, mapTypeControl: false, streetViewControl: false }}
      >
        {pickup && <Marker position={pickup} label="P" title="Pickup" />}
        {dropoff && <Marker position={dropoff} label="D" title="Dropoff" />}
        {roadPath && (
          <Polyline
            path={roadPath}
            options={{ strokeColor: "#3b82f6", strokeWeight: 4, strokeOpacity: 0.8 }}
          />
        )}
      </GoogleMap>
    </Box>
  );
}

function RouteMapFallback({ pickupAddress, dropoffAddress }) {
  if (!pickupAddress && !dropoffAddress) {
    return (
      <Paper sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, textAlign: "center" }}>
        <LocalShipping sx={{ fontSize: 28, color: "#94a3b8", mb: 0.5 }} />
        <Typography variant="caption" sx={{ color: "#94a3b8" }}>Route unavailable</Typography>
      </Paper>
    );
  }

  const origin = encodeURIComponent(pickupAddress || "unknown");
  const dest = encodeURIComponent(dropoffAddress || "unknown");
  const mapsUrl = `https://www.google.com/maps/dir/${origin}/${dest}`;

  return (
    <Paper
      component="a"
      href={mapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        p: 3,
        bgcolor: "#f8fafc",
        borderRadius: 2,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        textDecoration: "none",
        border: "1px dashed #cbd5e1",
        cursor: "pointer",
        transition: "all 0.18s ease",
        "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" },
      }}
    >
      <LocalShipping sx={{ fontSize: 32, color: "#64748b" }} />
      <Typography variant="body2" fontWeight={600} sx={{ color: "#334155" }}>
        View Route on Google Maps
      </Typography>
      <Typography variant="caption" sx={{ color: "#94a3b8" }}>
        {pickupAddress} → {dropoffAddress}
      </Typography>
    </Paper>
  );
}

const AgentOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const agent = JSON.parse(localStorage.getItem("user"));
  const { showSnackbar } = useSnackbar();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [nextStatus, setNextStatus] = useState(null);
  // Agent-provided delivery days (customer estimate = prep time + these days)
  const [deliveryDaysInput, setDeliveryDaysInput] = useState("");
  const [savingDeliveryDays, setSavingDeliveryDays] = useState(false);

  // Keep the input in sync with the saved value (initial fetch or external update)
  useEffect(() => {
    const saved = order?.deliveryPhase?.agentDeliveryDays;
    setDeliveryDaysInput(saved ?? "");
  }, [order?.deliveryPhase?.agentDeliveryDays]);

  const saveDeliveryDays = async () => {
    const days = Number(deliveryDaysInput);
    if (!Number.isInteger(days) || days < 0 || days > 365) {
      showSnackbar("Enter days between 0 and 365", "error");
      return;
    }
    try {
      setSavingDeliveryDays(true);
      const { data } = await api.patch(`/agent/orders/${orderId}/delivery-date`, {
        days,
      });
      setOrder((prev) => ({
        ...prev,
        deliveryPhase: {
          ...prev.deliveryPhase,
          agentDeliveryDays: data.agentDeliveryDays,
        },
      }));
      showSnackbar("Delivery days saved", "success");
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to save delivery days",
        "error"
      );
    } finally {
      setSavingDeliveryDays(false);
    }
  };

  useEffect(() => {
    if (!agent?._id) {
      navigate("/agent/login", { replace: true });
      return;
    }
    return () => {
      console.log("Agent session cleanup");
    };
  }, [agent, navigate]);

  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/agent/orders/${orderId}`);
      setOrder(data);
    } catch (error) {
      console.error("Failed to load order:", error);
      navigate("/agent/orders", { replace: true });
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [orderId, navigate]);

  useEffect(() => {
    if (agent?._id && orderId) {
      fetchOrder();
    }
  }, [agent?._id, orderId, fetchOrder]);

  useEffect(() => {
    if (!orderId) return;

    // Join the order room as soon as the page mounts — socket.io buffers the
    // emit until connected — so order_status_updated events arrive without
    // waiting on the fetch below or a reconnect.
    socket.emit("join_order", orderId);

    const handleOrderUpdate = (data) => {
      if (data.orderId === orderId) {
        setOrder((prev) => ({
          ...prev, status: data.status, tracking: data.tracking,
          deliveryPhase: data.deliveryPhase || prev.deliveryPhase,
          agentPhase: data.agentPhase || prev.agentPhase,
          vendorPhase: data.vendorPhase || prev.vendorPhase,
        }));
      }
    };
    const handlePreparationUpdate = (data) => {
      if (data.orderId === orderId) {
        setOrder((prev) => ({ ...prev, status: data.status || prev.status, tracking: data.tracking || prev.tracking, vendorPhase: data.vendorPhase || prev.vendorPhase, preparationUpdates: data.preparationUpdates || prev.preparationUpdates }));
      }
    };
    const handleVendorPreparing = (data) => {
      if (data.orderId === orderId) {
        setOrder((prev) => ({ ...prev, status: data.status || prev.status, vendorPhase: data.vendorPhase || prev.vendorPhase, tracking: data.tracking || prev.tracking, preparationUpdates: data.preparationUpdates || prev.preparationUpdates }));
      }
    };
    const handleVendorReady = (data) => {
      if (data.orderId === orderId) {
        setOrder((prev) => ({ ...prev, status: data.status || prev.status, vendorPhase: data.vendorPhase || prev.vendorPhase, tracking: data.tracking || prev.tracking, preparationUpdates: data.preparationUpdates || prev.preparationUpdates }));
      }
    };
    const handleConnect = () => {
      socket.emit("join_order", orderId);
    };

    socket.on("order_status_updated", handleOrderUpdate);
    socket.on("preparation_update", handlePreparationUpdate);
    socket.on("vendor_preparing", handleVendorPreparing);
    socket.on("vendor_ready", handleVendorReady);
    socket.on("connect", handleConnect);
    return () => {
      socket.off("order_status_updated", handleOrderUpdate);
      socket.off("preparation_update", handlePreparationUpdate);
      socket.off("vendor_preparing", handleVendorPreparing);
      socket.off("vendor_ready", handleVendorReady);
      socket.off("connect", handleConnect);
    };
  }, [orderId, agent?._id, fetchOrder]);

  const normalizeStatusValue = (value = "") => {
    return String(value || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  // Vendor-phase tokens that mean "items are ready for the agent to pick up".
  const VENDOR_READY_STATES = ["VENDOR_READY", "READY_FOR_PICKUP", "ITEMS_READY_FOR_PICKUP"];

  const getCurrentOrderStatus = (order) => {
    const status = order.deliveryPhase?.status || order.status;
    let normalizedStatus =
      !status || status === "NONE" || status === "UNKNOWN"
        ? "PENDING"
        : normalizeStatusValue(status);

    const tracking = Array.isArray(order.tracking) ? order.tracking : [];
    if (tracking.length > 0) {
      const lastTracking = tracking[tracking.length - 1];
      const candidate =
        lastTracking?.status ||
        lastTracking?.label ||
        lastTracking?.key ||
        lastTracking?.code;
      const normalizedTracking = normalizeStatusValue(candidate);
      if (VENDOR_READY_STATES.includes(normalizedTracking)) {
        normalizedStatus = "ASSIGNED";
      }
    }

    if (VENDOR_READY_STATES.includes(normalizedStatus)) {
      normalizedStatus = "ASSIGNED";
    }

    return normalizedStatus;
  };

  const isOrderClosed = (o) =>
    o.status === "CANCELLED" ||
    o.vendorPhase?.status === "CANCELLED" ||
    o.vendorPhase?.status === "REJECTED";

  // Vendor has marked the order ready (vendor phase, last tracking entry, or raw
  // delivery status) — required before the agent may pick up.
  const isVendorReady = (o) => {
    if (!o) return false;
    if (VENDOR_READY_STATES.includes(normalizeStatusValue(o.vendorPhase?.status))) return true;
    const tracking = Array.isArray(o.tracking) ? o.tracking : [];
    const last = tracking[tracking.length - 1];
    const lastKey = normalizeStatusValue(last?.status || last?.label || last?.key || last?.code);
    if (VENDOR_READY_STATES.includes(lastKey)) return true;
    return VENDOR_READY_STATES.includes(normalizeStatusValue(o.deliveryPhase?.status || o.status));
  };

  const getNextDeliveryAction = () => {
    // A cancelled/rejected order can no longer be updated by the agent
    if (isOrderClosed(order)) {
      return null;
    }
    const deliveryPhase = getCurrentOrderStatus(order);
    const actions = {
      NONE: "PICKED_UP",
      PENDING: "PICKED_UP",
      ASSIGNED: "PICKED_UP",
      PICKED_UP: "IN_TRANSIT",
      IN_TRANSIT: "DELIVERED",
    };
    const action = actions[deliveryPhase] || null;
    // The agent may only pick up after the vendor marks the order ready.
    if (action === "PICKED_UP" && !isVendorReady(order)) return null;
    return action;
  };

  const getDeliveryActionLabel = (action) => {
    const labels = {
      PICKED_UP: "Mark as Picked Up",
      IN_TRANSIT: "Mark as In Transit",
      DELIVERED: "Mark as Delivered",
    };
    return labels[action] || "Update Status";
  };

  const handleUpdateStatus = async () => {
    if (!nextStatus || isOrderClosed(order)) return;
    // Defense in depth: never POST a pickup the vendor hasn't readied yet.
    if (nextStatus === "PICKED_UP" && !isVendorReady(order)) return;
    try {
      setLoading(true);
      const endpointMap = { PICKED_UP: "picked_up", IN_TRANSIT: "in_transit", DELIVERED: "delivered" };
      await api.post(`/agent/orders/${orderId}/${endpointMap[nextStatus]}`, {});
      await fetchOrder();
      setConfirmDialog(false);
      setNextStatus(null);
      showSnackbar("Order status updated successfully!", "success");
    } catch (error) {
      console.error("Failed to update status:", error);
      showSnackbar(error.response?.data?.message || "Failed to update status", "error");
    } finally { setLoading(false); }
  };

  const getStatusLabel = (status) => status ? status.replace(/_/g, " ") : "";

  const getOrderVendor = (o) => {
    if (!o) return null;

    const candidates = [];
    for (const item of (o.items || [])) {
      if (item?.vendorId && typeof item.vendorId === 'object') candidates.push(item.vendorId);
      if (item?.vendor && typeof item.vendor === 'object') candidates.push(item.vendor);
      if (item?.vendorDetails && typeof item.vendorDetails === 'object') candidates.push(item.vendorDetails);
    }

    if (o?.vendorId && typeof o.vendorId === 'object') candidates.push(o.vendorId);
    if (o?.vendor && typeof o.vendor === 'object') candidates.push(o.vendor);
    if (o?.vendorDetails && typeof o.vendorDetails === 'object') candidates.push(o.vendorDetails);

    return candidates.find(Boolean) || null;
  };

  const pickupLocation = useMemo(() => {
    const vendor = getOrderVendor(order);
    const addr = vendor?.store?.address || vendor?.address || vendor?.store || order?.vendorAddress || order?.vendorAddressData;
    return getLatLng(addr);
  }, [order]);

  const dropoffLocation = useMemo(() => {
    const addr = order?.address || order?.shippingAddress || order?.deliveryAddress || order?.customerAddress || order?.deliveryLocation || order?.location;
    return getLatLng(addr);
  }, [order]);

  if (initialLoading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
        <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #0062ff 100%)", px: { xs: 2, sm: 4 }, py: 2.5 }}>
          <Button onClick={() => navigate("/agent/orders", { state: { activeTab: location.state?.fromTab || 0 } })}
            sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 1, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
            <ArrowBack sx={{ fontSize: 16 }} /> Back to Orders
          </Button>
          <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", mb: 0.5 }}>Order Details</Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>Manage and track this delivery</Typography>
        </Box>
        <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4, mt: 3 }}>
          <LoadingScreen message="Loading order details..." />
        </Box>
      </Box>
    );
  }

  if (!order) return null;

  const vendor = getOrderVendor(order);
  const vendorName = vendor?.store?.businessName || vendor?.name || "—";
  const vendorAddress = vendor?.store?.address || vendor?.address || vendor?.store;
  const vendorAddressStr = getAddressText(vendorAddress) || null;

  const deliveryAddr = order.address || order.shippingAddress || order.deliveryAddress;
  let customerName = "—";
  const user = order.userId || order.customer;
  if (user) {
    if (typeof user === 'string') { customerName = user; }
    else { customerName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.name || user.username || "—"; }
  }
  if (customerName === "—" && deliveryAddr) { customerName = deliveryAddr.name || deliveryAddr.recipientName || deliveryAddr.contactName || "—"; }
  if (customerName === "—" && typeof order.customer === 'string') { customerName = order.customer; }

  const deliveryAddressStr = getAddressText(deliveryAddr) || "—";

  const statusSteps = [
    { key: "ASSIGNED", label: "Assigned" },
    { key: "PICKED_UP", label: "Picked Up" },
    { key: "IN_TRANSIT", label: "In Transit" },
    { key: "DELIVERED", label: "Delivered" },
  ];
  const currentStatus = getCurrentOrderStatus(order);
  const currentStepIndex = statusSteps.findIndex((s) => s.key === currentStatus);

  // 🍅 Delivery date mandate — single-source flag from the agent order API
  // (computed server-side so the rule can never drift from the backend gates)
  const deliveryRequired = Boolean(order.deliveryDateRequired);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #0062ff 100%)", px: { xs: 2, sm: 4 }, py: 2.5 }}>
        <Button onClick={() => navigate("/agent/orders", { state: { activeTab: location.state?.fromTab || 0 } })}
          sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 1, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
          <ArrowBack sx={{ fontSize: 16 }} /> Back to Orders
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", mb: 0.5 }}>Order # {order._id} </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>Manage and track this delivery</Typography>
      </Box>

      <Box sx={{ width: '100%', px: { xs: 2, sm: 4 }, py: 3, boxSizing: 'border-box', overflow: 'hidden' }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* ═════════ LEFT COLUMN ═════════ */}
          <Grid item xs={12} md={8}>
            {/* Order Summary */}
            <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(15,23,42,0.07)", mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>📦 Order Summary</Typography>
                <Grid container spacing={2} sx={{ mb: 2.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 500, display: "block", mb: 0.3 }}>Order Total</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: "#1976d2" }}>{formatCurrency(order.total)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 500, display: "block", mb: 0.3 }}>Payment Method</Typography>
                    <Chip label={order.paymentMethod?.toUpperCase() || "N/A"} size="small" sx={{ fontWeight: 600, fontSize: "0.7rem", bgcolor: "rgba(25,118,210,0.08)", color: "#1976d2", border: "none" }} />
                  </Grid>
                </Grid>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b", mb: 1.5 }}>Items ({order.items.length})</Typography>
                <Stack spacing={1}>
                  {order.items.map((item, idx) => (
                    <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", p: 1.25, bgcolor: "#f8fafc", borderRadius: 1.5 }}>
                      <Box><Typography variant="body2" fontWeight={500} sx={{ color: "#334155" }}>{item.title}</Typography><Typography variant="caption" sx={{ color: "#94a3b8" }}>Qty: {item.qty}</Typography></Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b" }}>{formatCurrency(item.price)}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            {/* Vendor Preparation */}
            {order.vendorPhase?.status && (
              <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(15,23,42,0.07)", mb: 3, border: "1px solid #ede9fe", bgcolor: "#faf7fc" }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "#6a1b9a", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>👨‍🍳 Vendor Status</Typography>
                  <Paper sx={{ p: 1.5, bgcolor: "#fff", borderRadius: 2, border: "1px solid #e0bee7", mb: 2 }}>
                    <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 500, display: "block", mb: 0.5 }}>Current Status</Typography>
                    <Chip label={order.vendorPhase.status?.replace(/_/g, " ")} color={order.vendorPhase.status === "READY_FOR_PICKUP" ? "success" : order.vendorPhase.status === "PREPARING" ? "warning" : "default"} sx={{ fontWeight: 600, fontSize: "0.72rem" }} />
                  </Paper>
                  {/* {order.vendorPhase.estimatedReadyTime && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 500, display: "block", mb: 0.5 }}><AccessTime sx={{ fontSize: 13, mr: 0.3, verticalAlign: "middle" }} /> Estimated Ready Time</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: "#334155" }}>{new Date(order.vendorPhase.estimatedReadyTime).toLocaleTimeString()}</Typography>
                    </Box>
                  )} */}
                  {order.preparationUpdates?.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e2e8f0" }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b", mb: 1.5 }}>Preparation Updates</Typography>
                      <Stack spacing={1.5}>
                        {order.preparationUpdates.map((update, idx) => (
                          <Paper key={idx} sx={{ p: 1.5, bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography variant="caption" fontWeight={600} sx={{ color: "#475569" }}>{update.addedBy}</Typography>
                              <Typography variant="caption" sx={{ color: "#94a3b8" }}>{new Date(update.createdAt).toLocaleString()}</Typography>
                            </Box>
                            {update.imageUrl && <Box sx={{ mb: 1, borderRadius: 1.5, overflow: "hidden", bgcolor: "#f5f5f5" }}><img src={update.imageUrl} alt="Preparation" style={{ width: "100%", maxHeight: "200px", objectFit: "cover" }} /></Box>}
                            {update.notes && <Typography variant="body2" sx={{ color: "#475569", whiteSpace: "pre-wrap" }}>{update.notes}</Typography>}
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            <Box sx={{ display: { xs: 'block', sm: 'flex' }, gap: 2, mb: 3 }}>
              <Box sx={{ width: { xs: '100%', sm: '50%' }, mb: { xs: 2, sm: 0 }, display: 'flex' }}>
                <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(15,23,42,0.07)", width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, flex: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>🏪 Pickup Location</Typography>
                    <Paper sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #ede9fe" }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Storefront sx={{ fontSize: 18, color: "#7c3aed", mt: 0.3, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography variant="body2" fontWeight={600} sx={{ color: "#334155", wordWrap: 'break-word', overflowWrap: 'break-word' }}>{vendorName}</Typography>
                          {vendorAddressStr && <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0.3, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{vendorAddressStr}</Typography>}
                          {order.agent?.distanceKm && <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5 }}><LocalShipping sx={{ fontSize: 12, mr: 0.3, verticalAlign: "middle" }} /> {order.agent.distanceKm} km away</Typography>}
                        </Box>
                      </Box>
                    </Paper>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '50%' }, display: 'flex' }}>
                <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(15,23,42,0.07)", width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, flex: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>📍 Delivery Address</Typography>
                    <Paper sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Person sx={{ fontSize: 18, color: "#1976d2", mt: 0.3, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography variant="body2" fontWeight={600} sx={{ color: "#334155", wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customerName}</Typography>
                          <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0.3, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{deliveryAddressStr}</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* 📅 Provide Delivery Date (agent-specified delivery days) */}
            {order.deliveryPhase?.status !== "DELIVERED" &&
              order.vendorPhase?.status !== "CANCELLED" &&
              order.vendorPhase?.status !== "REJECTED" &&
              order.status !== "CANCELLED" && (
              <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(15,23,42,0.07)", mt: 3, mb: 3 }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    📅 Delivery Date
                  </Typography>
                  {deliveryRequired && (
                    <Box sx={{ p: 1.5, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 2, mb: 1.5 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: "#b91c1c" }}>
                        ⚠ Delivery date is required — the vendor has started preparing this order.
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mb: 2 }}>
                    The customer's estimated delivery = order preparation date + vendor prep time + the days you enter below.
                  </Typography>
                  <Paper sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTime sx={{ fontSize: 20, color: "#7c3aed", flexShrink: 0 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: "#334155" }}>
                        Vendor prep: {order.estimatedPrepDays ?? 0} day(s)
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>
                        Items ready by (estimated):{" "}
                        {order.estimatedPrepDate
                          ? new Date(order.estimatedPrepDate).toLocaleDateString("en-IN", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })
                          : "—"}
                      </Typography>
                    </Box>
                  </Paper>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <TextField
                      type="number"
                      label="Delivery days"
                      required={deliveryRequired}
                      size="small"
                      value={deliveryDaysInput}
                      onChange={(e) => setDeliveryDaysInput(e.target.value)}
                      inputProps={{ min: 0, max: 365, step: 1 }}
                      sx={{ width: 170 }}
                    />
                    <Button
                      variant="contained"
                      disabled={savingDeliveryDays || deliveryDaysInput === ""}
                      onClick={saveDeliveryDays}
                      sx={{ bgcolor: "#ffa41c", color: "#111", fontWeight: 700, textTransform: "none", "&:hover": { bgcolor: "#f90" } }}
                    >
                      {savingDeliveryDays
                        ? "Saving…"
                        : order.deliveryPhase?.agentDeliveryDays != null
                        ? "Update"
                        : "Save"}
                    </Button>
                  </Box>
                  {(() => {
                    const days = Number(deliveryDaysInput);
                    const hasDays =
                      deliveryDaysInput !== "" &&
                      Number.isInteger(days) &&
                      days >= 0;
                    let previewDate = null;
                    // Same anchor as backend: "Order Being Prepared" tracking
                    // entry time, falling back to acceptance → order date.
                    const prepStart =
                      order.tracking?.find(
                        (t) => t?.key === TRACKING_VENDOR_PREPARING
                      )?.time ||
                      order.vendorPhase?.acceptedAt ||
                      order.createdAt;
                    if (hasDays && prepStart) {
                      previewDate = new Date(prepStart);
                      previewDate.setDate(
                        previewDate.getDate() +
                          (order.estimatedPrepDays ?? 0) +
                          days
                      );
                    }
                    return (
                      <Paper
                        sx={{
                          p: 1.5,
                          bgcolor: "#f8fafc",
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          mt: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <LocalShipping
                          sx={{ fontSize: 20, color: "#1976d2", flexShrink: 0 }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: "#334155" }}>
                            Estimated delivery:{" "}
                            {previewDate
                              ? previewDate.toLocaleDateString("en-IN", {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : "—"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>
                            Read-only preview — order preparation date + vendor prep + your days (what the customer will see).
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })()}
                  {order.deliveryPhase?.agentDeliveryDays != null && (
                    <Chip
                      label={`Saved: ${order.deliveryPhase.agentDeliveryDays} day(s)`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ mt: 1.5, fontWeight: 600 }}
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Route Map - now below Delivery Address */}
            <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(15,23,42,0.07)" }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>🗺️ Route Map</Typography>
                <RouteMap pickupAddress={vendorAddressStr} dropoffAddress={deliveryAddressStr} pickupCoords={pickupLocation} dropoffCoords={dropoffLocation} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1.5, justifyContent: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#7c3aed", flexShrink: 0 }} /><Typography variant="caption" sx={{ color: "#64748b" }}>Pickup (P)</Typography></Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#1976d2", flexShrink: 0 }} /><Typography variant="caption" sx={{ color: "#64748b" }}>Dropoff (D)</Typography></Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 20, height: 3, borderRadius: 1, bgcolor: "#3b82f6", flexShrink: 0 }} /><Typography variant="caption" sx={{ color: "#64748b" }}>Route</Typography></Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ═════════ RIGHT COLUMN ═════════ */}
          <Grid item xs={12} md={4}>
            {/* Delivery Status */}
            <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(15,23,42,0.07)", mb: 3, width: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                {/* <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b", mb: 2.5, display: "flex", alignItems: "center", gap: 1 }}>🚚 Delivery Status</Typography>
                <Box sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: 2, mb: 2.5 }}>
                  {statusSteps.map((step, idx) => {
                    const isCompleted = idx <= currentStepIndex;
                    const isCurrent = idx === currentStepIndex;
                    return (
                      <React.Fragment key={step.key}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                          {isCompleted ? <CheckCircle sx={{ fontSize: 18, color: isCurrent ? "#f59e0b" : "#22c55e" }} /> : <RadioButtonUnchecked sx={{ fontSize: 18, color: "#cbd5e1" }} />}
                          <Typography variant="body2" sx={{ fontWeight: isCurrent ? 700 : isCompleted ? 600 : 400, color: isCompleted ? (isCurrent ? "#92400e" : "#166534") : "#94a3b8" }}>{step.label}</Typography>
                        </Box>
                        {idx < statusSteps.length - 1 && <Box sx={{ ml: 1.9, width: 2, height: 16, bgcolor: isCompleted ? "#22c55e" : "#e2e8f0", borderRadius: 1 }} />}
                      </React.Fragment>
                    );
                  })}
                </Box> */}
                {/* <Box sx={{ mb: 2.5, p: 1.5, bgcolor: "#f8fafc", borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 500, display: "block", mb: 0.5 }}>Current Status</Typography>
                  <Chip icon={<LocalShipping />} label={currentStatus.replace(/_/g, " ")} color={currentStatus === "DELIVERED" ? "success" : "primary"} sx={{ fontWeight: 600, fontSize: "0.75rem", width: "100%" }} />
                </Box> */}
                {getNextDeliveryAction() && currentStatus !== "DELIVERED" ? (
                  <Box sx={{ mb: 2.5, p: 1.5, bgcolor: "#f0fdf4", borderRadius: 2, border: "1px solid #dcfce7" }}>
                    <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600, display: "block", mb: 0.5 }}><ArrowForward sx={{ fontSize: 13, mr: 0.3, verticalAlign: "middle" }} />Your Next Step</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: "#166534", mb: 1.5 }}>{getDeliveryActionLabel(getNextDeliveryAction())}</Typography>
                    <Button variant="contained" fullWidth onClick={() => { setNextStatus(getNextDeliveryAction()); setConfirmDialog(true); }} disabled={loading} sx={{ bgcolor: "#16a34a", borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: "0.82rem", py: 0.8, "&:hover": { bgcolor: "#15803d" } }}>
                      {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : getDeliveryActionLabel(getNextDeliveryAction())}
                    </Button>
                  </Box>
                ) : ["NONE", "PENDING", "ASSIGNED"].includes(currentStatus) && !isVendorReady(order) && !isOrderClosed(order) ? (
                  <Box sx={{ mb: 2.5, p: 1.5, bgcolor: "#fffbeb", borderRadius: 2, border: "1px solid #fde68a" }}>
                    <Typography variant="caption" sx={{ color: "#b45309", fontWeight: 600, display: "block", mb: 0.5 }}>⏳ Your Next Step</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: "#92400e" }}>
                      Waiting for the vendor to mark the order ready for pick up
                    </Typography>
                  </Box>
                ) : currentStatus === "DELIVERED" ? (
                  <Box sx={{ mb: 2.5, p: 1.5, bgcolor: "#f0fdf4", borderRadius: 2, border: "1px solid #dcfce7", textAlign: "center" }}>
                    {/* <CheckCircle sx={{ fontSize: 32, color: "#16a34a", mb: 0.5 }} /> */}
                    <Typography variant="body2" fontWeight={700} sx={{ color: "#166534" }}>✓ Order Delivered</Typography>
                  </Box>
                ) : null}
                <Box sx={{ borderTop: "1px solid #e2e8f0", pt: 2.5, mt: 1 }}>
                  <Stack spacing={2}>
                    {order.agent?.distanceKm && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LocalShipping sx={{ fontSize: 16, color: "#64748b" }} />
                        <Box><Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>Distance</Typography><Typography variant="body2" fontWeight={600} sx={{ color: "#334155" }}>{order.agent.distanceKm} km</Typography></Box>
                      </Box>
                    )}
                    {order.agentCommission?.amount && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MonetizationOn sx={{ fontSize: 16, color: "#16a34a" }} />
                        <Box><Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>Commission</Typography><Typography variant="body2" fontWeight={700} sx={{ color: "#16a34a" }}>{formatCurrency(order.agentCommission.amount)}</Typography></Box>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Timer sx={{ fontSize: 16, color: "#64748b" }} />
                      <Box><Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>Agent Phase</Typography><Chip label={(order.agentPhase?.status || "NONE").replace(/_/g, " ")} size="small" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} /></Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Payment sx={{ fontSize: 16, color: "#64748b" }} />
                      <Box><Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>Payment Status</Typography><Chip label={order.payment?.status || "PENDING"} color={order.payment?.status === "PAID" ? "success" : "warning"} size="small" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} /></Box>
                    </Box>
                  </Stack>
                </Box>
              </CardContent>
            </Card>

            {/* Delivery Timeline - now below Delivery Status */}
            <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(15,23,42,0.07)", width: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>📋 Delivery Timeline</Typography>
                <Stack spacing={1.5}>
                  {order.tracking?.length > 0 ? (
                    order.tracking.map((track, idx) => (
                      <Box key={idx} sx={{ display: "flex", alignItems: "center", p: 1.5, bgcolor: track.completed ? "#f0fdf4" : "#fafafa", borderRadius: 2, borderLeft: track.completed ? "4px solid #22c55e" : "4px solid #cbd5e1" }}>
                        <CheckCircle sx={{ fontSize: 20, color: track.completed ? "#22c55e" : "#cbd5e1", mr: 1.5, flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b" }}>{getStatusLabel(track.key || track.label || track.status)}</Typography>
                          <Typography variant="caption" sx={{ color: "#94a3b8" }}>{track.time ? new Date(track.time).toLocaleString() : ""}</Typography>
                        </Box>
                        {track.completed && <Chip label="Completed" color="success" size="small" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} />}
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ p: 3, textAlign: "center" }}><Typography variant="body2" sx={{ color: "#94a3b8" }}>No tracking updates available yet</Typography></Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} PaperProps={{ sx: { borderRadius: 3, maxWidth: 400, width: "100%" } }}>
          <DialogTitle sx={{ pb: 1 }}><Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b" }}>Update Delivery Status?</Typography></DialogTitle>
          <DialogContent sx={{ pb: 2 }}>
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, mb: 1 }}>
              <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 500, display: "block", mb: 1 }}>Action</Typography>
              <Chip label={getDeliveryActionLabel(nextStatus)} color="primary" variant="outlined" sx={{ fontWeight: 600, fontSize: "0.75rem", mb: 2 }} />
              <Box sx={{ borderTop: "1px solid #e2e8f0", pt: 2 }}>
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mb: 0.3 }}>Order #{orderId.substring(0, 8).toUpperCase()}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: "#1976d2" }}>{formatCurrency(order.total)}</Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.82rem" }}>
              {nextStatus === "DELIVERED" ? "This will mark the order as delivered and complete the delivery process." : `This will update the order status to "${getDeliveryActionLabel(nextStatus)}".`}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => setConfirmDialog(false)} disabled={loading} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2 }}>Cancel</Button>
            <Button onClick={handleUpdateStatus} variant="contained" disabled={loading} sx={{ bgcolor: "#16a34a", borderRadius: "10px", textTransform: "none", fontWeight: 600, px: 3, "&:hover": { bgcolor: "#15803d" } }}>
              {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AgentOrderDetails;